import { useEffect, useState } from "react";
import { faFloppyDisk } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";

import ApiConnector from "../connector/ApiConnector";
import UtilService from "../service/UtilService";

import { makeKey, useTableModel } from "../service/TableModelUtilService";

function normalizeOdometers(rows) {
  return rows
    .map((r) => ({
      value: r.value === "" ? "" : Number(r.value ?? 0),
      status: String(r.status ?? ""),
      isCreatedByAutoUpdate: !!r.isCreatedByAutoUpdate,
    }))
    .sort((a, b) => {
      const s = a.status.localeCompare(b.status);
      if (s !== 0) return s;
      const av = a.value === "" ? Number.NEGATIVE_INFINITY : a.value;
      const bv = b.value === "" ? Number.NEGATIVE_INFINITY : b.value;
      if (av !== bv) return av - bv;
      return a.isCreatedByAutoUpdate === b.isCreatedByAutoUpdate
        ? 0
        : a.isCreatedByAutoUpdate
        ? 1
        : -1;
    });
}

const columns = [
  {
    id: "value",
    accessor: (r) => (r.value === "" ? Number.NEGATIVE_INFINITY : Number(r.value ?? 0)),
    comparator: (a, b) => a - b,
    searchable: true,
    filterValue: (v, r) => String(r.value ?? ""),
  },
  {
    id: "status",
    accessor: (r) => String(r.status ?? ""),
    searchable: true,
  },
  {
    id: "created",
    accessor: (r) => (r.isCreatedByAutoUpdate ? 1 : 0),
    comparator: (a, b) => a - b,
    searchable: true,
    filterValue: (v, r) => (r.isCreatedByAutoUpdate ? "yes" : "no"),
  },
];

const NEW_ENTRY = {
  value: 0,
  status: "WARM",
  isCreatedByAutoUpdate: false,
};

const SortHeader = ({ column, sortBy, sortDir, onToggle, children }) => (
  <button
    type="button"
    className={`table-sorter ${sortBy === column ? `active ${sortDir}` : ""}`}
    onClick={() => onToggle(column)}
    title="Click to sort / toggle"
    style={{
      background: "transparent",
      border: 0,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: 0,
      margin: 0,
      whiteSpace: "nowrap",
      fontWeight: "bold",
      fontSize: "1rem",
      letterSpacing: "normal",
      color: "inherit",
    }}
  >
    {children}
    <span style={{ fontSize: "0.85em", opacity: 0.7, marginLeft: 4 }}>
      {sortBy === column ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
    </span>
  </button>
);

export default function Component() {
  const {
    rows,
    visibleRows,
    sortBy,
    sortDir,
    setRows,
    setFilter,
    toggleSort,
    addRow,
    updateRow,
    removeRow,
    applyCurrentSort,
    setBaselineFromRows,
    canSave,
    keyField,
  } = useTableModel({
    initialRows: [],
    columns,
    keyField: "_key",
    initialSortBy: null, // no default sorting in the UI
    initialSortDir: "asc",
    normalizeForSignature: normalizeOdometers,
  });

  const [csvUrl, setCsvUrl] = useState("");

  // --- Load data + set baseline signature + prepare CSV (with cleanup) ---
  useEffect(() => {
    let revoked = false;
    let previousUrl = null;

    async function load() {
      const tRaw = await ApiConnector.getOdometers();

      // Default sorting mimics the old version:
      // secondary sort by value (asc), primary sort by status (A→Z), relying on stable sort
      const tSorted = [...tRaw]
        .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
        .sort((a, b) =>
          String(a.status ?? "").localeCompare(String(b.status ?? ""))
        );

      // Add _key only after sorting
      const withKeys = tSorted.map((r) =>
        r._key ? r : { ...r, _key: makeKey() }
      );

      setRows(withKeys);
      setBaselineFromRows(withKeys);

      // Generate CSV object URL and keep a reference for revoking on cleanup
      const url = UtilService.convertJsonArrayToDownloadableCsvFileUrl(tRaw);
      previousUrl = url;
      setCsvUrl(url);
    }

    load();

    return () => {
      if (previousUrl && !revoked) {
        try {
          URL.revokeObjectURL(previousUrl);
        } catch {}
        revoked = true;
      }
    };
  }, [setRows, setBaselineFromRows]);

  const onChangeValue = (row, nextVal) => {
    const key = row[keyField];
    const num = nextVal === "" ? "" : Number(nextVal);
    updateRow(key, { value: num });
  };

  const onChangeStatus = (row, nextStatus) => {
    const key = row[keyField];
    updateRow(key, { status: nextStatus });
  };

  const onRemove = (row) => {
    removeRow(row[keyField]);
  };

  const onAdd = () => {
    addRow(NEW_ENTRY);
  };

  // --- Save handler ---
  const save = async () => {
    if (!canSave) return;

    const result = window.confirm(
      "New odometer values will be set. This operation can't be undone.",
    );
    if (!result) return;
    applyCurrentSort();
    const payload = rows.map(({ _key, ...rest }) => rest);
    await ApiConnector.replaceOdometers(payload);
    setBaselineFromRows(rows);
  };

  return (
    <>
      <header className="subheader header-with-save">
        <h1 className="header-title">Config - Odometer rule</h1>
        <span className="header-save">
          <FontAwesomeIcon
            icon={faFloppyDisk}
            size="2xl"
            className="icon-clickable"
            onClick={canSave ? save : undefined}
            title={canSave ? "Save changes" : "No changes to save"}
            style={{
              opacity: canSave ? 1 : 0.4,
              cursor: canSave ? "pointer" : "not-allowed",
              pointerEvents: canSave ? "auto" : "none",
            }}
            aria-disabled={!canSave}
          />
        </span>
      </header>

      <main>
        <div
          className="row"
          style={{
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 0,
          }}
        >
          <div className="col">
            <h2 style={{ marginBottom: 6 }}>Odometer values</h2>
          </div>
          <div className="col" />
        </div>

        <div style={{ margin: "0 0 12px" }}>
          <a href={csvUrl} download={"odometers.csv"}>
            Export to XLSX
          </a>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <input
              type="text"
              placeholder="Filter by displayed odometer values..."
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: 320, maxWidth: "100%" }}
            />
          </div>
        </div>

        <table className="config-table table-with-button">
          <thead>
            <tr>
              <th>
                <SortHeader
                  column="value"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onToggle={toggleSort}
                >
                  Odometer value
                </SortHeader>
              </th>
              <th>
                <SortHeader
                  column="status"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onToggle={toggleSort}
                >
                  Status
                </SortHeader>
              </th>
              <th>
                <SortHeader
                  column="created"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onToggle={toggleSort}
                >
                  Created by auto update
                </SortHeader>
              </th>
              <th style={{ width: 44, textAlign: "right" }}>
                <FontAwesomeIcon
                  icon={faPlus}
                  className="icon-clickable"
                  onClick={onAdd}
                />
              </th>
            </tr>
          </thead>

          <tbody>
            {visibleRows.map((row) => (
              <tr key={row._key}>
                <td>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={row.value ?? ""}
                    onChange={(e) => onChangeValue(row, e.target.value)}
                  />
                </td>
                <td>
                  <select
                    value={row.status ?? "WARM"}
                    onChange={(e) => onChangeStatus(row, e.target.value)}
                  >
                    <option value="WARM">WARM</option>
                    <option value="HOT">HOT</option>
                    <option value="NEW_WARM">NEW_WARM</option>
                    <option value="NEW_HOT">NEW_HOT</option>
                  </select>
                </td>
                <td>{row.isCreatedByAutoUpdate ? "YES" : "-"}</td>
                <td style={{ width: 44, textAlign: "right" }}>
                  <FontAwesomeIcon
                    icon={faTrashCan}
                    className="icon-clickable"
                    onClick={() => onRemove(row)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </>
  );
}
